import classNames from 'classnames';
import React, { CSSProperties, useCallback, useState, useRef, useEffect } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { abTestState, pageContextState, pageSubContextState } from '../recoil/atoms';
import { PageContext } from '../enums/PageContext.enum';
import { PageSubContext } from '../enums/PageSubContext.enum';
import { SelectionType } from '../enums/SelectionType.enum';
import { PossibleStyle, useProductStyles } from '../hooks/useProductStyles';
import { SizingToolState } from '../models/actionStates';
import ProductFormButton from '../product_detail/components/ProductFormButton';
import { isMobileState, sizingToolState } from '../recoil/selectors';
import { DivButton } from '../root/components/DivButton';
import { ProductColorSwatches } from './ProductColorSwatches';
import { ProductFitSwatches } from './ProductFitSwatches';
import { ProductSizeSwatches } from './ProductSizeSwatches';
import { ProductControlProps } from './ProductControls';
import { QuantityDropdown } from './QuantityDropdown';
import { useProductSelection } from '../hooks/useProductSelection';
import styles from './ProductControlsMobile.module.scss';
import { Button } from '../root/components/buttons/Button';
import { Icon } from '../icons/Icon';
import { hlViewportLocked, scrollToRef } from '../util';
// needed for AB2022-04, feel free to remove afterwards
import 'vh-check';

const baseToggleStyle: CSSProperties = {
  padding: '10px',
  paddingTop: '12px',
  fontSize: '13px',
  fontWeight: 500,
  textAlign: 'center',
  borderTopLeftRadius: '2px',
  borderTopRightRadius: '2px',
};

const activeStyle: CSSProperties = {
  ...baseToggleStyle,
  border: '1px solid black',
  borderBottom: '1px solid transparent',
  zIndex: 1,
  color: '#6666CC',
};

const inactiveStyle: CSSProperties = {
  ...baseToggleStyle,
};

const StyleToggle: React.FC<{ productStyles: PossibleStyle[]; selectionType: SelectionType; selectionKey: string }> = ({
  productStyles,
  selectionType,
  selectionKey,
}) => {
  const { handle, setHandle } = useProductSelection(selectionType, selectionKey);
  const abTests = useRecoilValue(abTestState);

  return productStyles.length > 0 ? (
    <div className={classNames(styles.toggleContainer, { [styles.multiline]: abTests['AB2022-12'] })}>
      <div></div>
      {productStyles.map((s, i) => {
        const isActive = s.handle === handle;
        return (
          <DivButton key={i} onClick={() => setHandle(s.handle)} style={isActive ? activeStyle : inactiveStyle} label={`Select product: ${s.title}`}>
            {s.title?.toUpperCase()}
          </DivButton>
        );
      })}
      <div></div>
    </div>
  ) : (
    <></>
  );
};

export const ProductControlsMobile: React.FC<ProductControlProps> = ({ selectionType, selectionKey, style, simplified }) => {
  const { product } = useProductSelection(selectionType, selectionKey);
  const productStyles = useProductStyles(product);
  const [quantity, setQuantity] = useState<number>(1);
  const [isCtaSticky, setCtaSticky] = useState(false);
  const [isScrollToCtaSticky, setScrollToCtaSticky] = useState(false);
  const ctaOuterContainerRef = useRef<HTMLDivElement>(null);
  const scrollCtaContainerRef = useRef<HTMLDivElement>(null);
  const setSizingToolState = useSetRecoilState(sizingToolState);
  const pageContext = useRecoilValue(pageContextState);
  const pageSubContext = useRecoilValue(pageSubContextState);
  const abTests = useRecoilValue(abTestState);
  const isMobile = useRecoilValue(isMobileState);
  const ab2022_02Active = abTests['AB2022-02'] && isMobile && pageContext === PageContext.LandingPage && pageSubContext !== PageSubContext.Homepage;
  const ab2022_04_1Active = abTests['AB2022-04-1'] && isMobile && pageContext === PageContext.ProductPage;
  const ab2022_04_2Active = abTests['AB2022-04-2'] && isMobile && pageContext === PageContext.ProductPage;
  if (ab2022_04_1Active || ab2022_04_2Active) {
    document.body.classList.add('sticky-button');
  } else {
    document.body.classList.remove('sticky-button');
  }
  const openSizingTool = useCallback(() => {
    setSizingToolState({
      sizingToolState: SizingToolState.Open,
      selectionType,
      selectionKey,
      initialProduct: product,
      product,
    });
  }, [product, setSizingToolState, selectionType]);
  const toggleStickyCta = useCallback(
    ({ bottom }) => {
      if (isCtaSticky && bottom + 80 >= window.innerHeight) {
        setCtaSticky(false);
      } else if (!isCtaSticky && bottom < window.innerHeight - 20) {
        setCtaSticky(true);
      }
    },
    [isCtaSticky],
  );
  const toggleStickyScrollToCta = useCallback(
    ({ bottom }) => {
      if (isScrollToCtaSticky && bottom >= window.innerHeight) {
        setScrollToCtaSticky(false);
      } else if (!isScrollToCtaSticky && bottom < window.innerHeight) {
        setScrollToCtaSticky(true);
      }
    },
    [isScrollToCtaSticky],
  );
  useEffect(() => {
    // use hlViewportLocked() to check if locked viewport conditional CSS is active for iOS 14
    const scrollEl = hlViewportLocked() ? document.querySelector('.hl-viewport') : window;
    const handleCtaScroll = () => {
      toggleStickyCta(ctaOuterContainerRef.current?.getBoundingClientRect());
    };
    const handleScrollToCtaScroll = () => {
      toggleStickyScrollToCta(scrollCtaContainerRef.current?.getBoundingClientRect());
    };
    if (ab2022_04_1Active) {
      handleCtaScroll();
      setScrollToCtaSticky(false);
      scrollEl?.addEventListener('scroll', handleCtaScroll);
    } else if (ab2022_04_2Active) {
      handleScrollToCtaScroll();
      setCtaSticky(false);
      scrollEl?.addEventListener('scroll', handleScrollToCtaScroll);
    }
    return () => {
      scrollEl?.removeEventListener('scroll', handleCtaScroll);
      scrollEl?.removeEventListener('scroll', handleScrollToCtaScroll);
    };
  }, [toggleStickyCta, toggleStickyScrollToCta, ab2022_04_1Active, ab2022_04_2Active]);
  if (simplified) {
    return (
      <div style={{ display: 'flex' }}>
        <QuantityDropdown style={{ width: 72 }} setQuantity={setQuantity} />
        <ProductFormButton selectionType={selectionType} selectionKey={selectionKey} quantity={quantity} />
      </div>
    );
  }
  return (
    <div style={style}>
      {productStyles && <StyleToggle productStyles={productStyles} selectionType={selectionType} selectionKey={selectionKey} />}
      <div className={classNames(styles.controlsWrapper, { [styles.styleToggle]: !!productStyles })}>
        <ProductColorSwatches product={product} selectionType={selectionType} selectionKey={selectionKey} isMobile={true} />
        <ProductFitSwatches product={product} selectionType={selectionType} selectionKey={selectionKey} style={{ marginTop: 10 }} />
        <ProductSizeSwatches product={product} selectionType={selectionType} selectionKey={selectionKey} style={{ marginTop: 20 }} />
        <div className={styles.sizingWrapper}>
          <Button onClick={openSizingTool} size="tiny" text="Sizing help" />
        </div>
        {!ab2022_02Active && (
          <div className={styles.ctaOuterContainer} ref={ctaOuterContainerRef}>
            <div className={classNames(styles.ctaContainer, { [styles.sticky]: isCtaSticky })}>
              {!ab2022_04_1Active && <QuantityDropdown style={{ width: 72, minWidth: 72 }} setQuantity={setQuantity} />}
              <ProductFormButton selectionType={selectionType} selectionKey={selectionKey} quantity={quantity} />
            </div>
            {ab2022_04_2Active && (
              <div className={styles.scrollToCtaOuterContainer}>
                <div className={styles.scrollToCtaBottomContainer} ref={scrollCtaContainerRef}>
                  <div className={classNames(styles.scrollToCtaButtonContainer, { [styles.sticky]: isScrollToCtaSticky })}>
                    <Button
                      text={
                        <>
                          Colors &amp; Sizes &nbsp;
                          <Icon name="chevron-up" style={{ width: '11px' }} />
                        </>
                      }
                      buttonType="primary"
                      size="medium"
                      onClick={() => scrollToRef(ctaOuterContainerRef, 60 - window.innerHeight)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductControlsMobile;
